/**
 * Rules for adder line items on non-solar projects (Quote page).
 *
 * A quick-quote project uses `quote_mode === QUICK_QUOTE` (see `util/quoteMode.ts`).
 * On such projects every included adder must satisfy ALL five rules below:
 *   - Adder type is Price  (type === 0)
 *   - Formula is Fixed     (formula === 'fixed' or formula === 0)
 *   - Show to customer     (show_customer === true)
 *   - Display as line item (display_as_line_item === true)
 *   - Tax inclusive        (value_includes_tax === true) — the unit price the pro
 *     types is the customer-facing price (e.g. "$10.00 Including $0.91 tax" on the
 *     proposal). Library adders saved as tax-exclusive are hidden so the on-quote
 *     total never disagrees with the proposal preview.
 *
 * Any violation:
 *   - hides the row in the adders Autocomplete library suggestions
 *   - blocks the save-on-submit in useQuoteLineItemsSaveOnSubmit (the SPA's
 *     Layer 2 guard) and surfaces a notify warning so the user can fix the row
 *   - and is also enforced server-side in
 *     `django/osapi/project/quote_system.py::set_quote_line_items_on_project`
 *     (Layer 3), which mirrors these same five rules.
 *
 * The rules originated in PR #19359 (later dropped from
 * `nsp-fpod-integration-branch`); only the pure-TS helpers were ported here so
 * the Quote page can reuse them without depending on Design-tab modules.
 */

export interface NonSolarAdderFieldErrors {
  type?: string
  formula?: string
  show_customer?: string
  display_as_line_item?: string
  value_includes_tax?: string
}

/** Matches backend `_adder_is_nsp_compatible` formula check (0, "0", "fixed"). */
function isFixedFormula(formula: number | string | null | undefined): boolean {
  if (formula == null) {
    return false
  }

  if (formula === 'fixed') {
    return true
  }

  const parsed = Number(formula)

  return Number.isFinite(parsed) && parsed === 0
}

export type NonSolarAdderRecord = {
  type: number | string
  formula: number | string
  show_customer: boolean
  display_as_line_item: boolean
  value_includes_tax: boolean
}

/** Fields read by {@link isAdderCompatibleWithNsp}; optional for loose API/list records. */
export type NonSolarAdderLike = {
  type?: number | string | null
  formula?: number | string | null
  show_customer?: boolean | null
  display_as_line_item?: boolean | null
  value_includes_tax?: boolean | null
}

/**
 * Return `true` when an adder record satisfies all five non-solar project rules.
 * Used for library filtering and save-time validation so rules cannot drift.
 */
export function isAdderCompatibleWithNsp(record: object): boolean {
  const { type, formula, show_customer, display_as_line_item, value_includes_tax } =
    record as NonSolarAdderLike
  return (
    Number(type) === 0 &&
    isFixedFormula(formula) &&
    !!show_customer &&
    !!display_as_line_item &&
    value_includes_tax === true
  )
}

/**
 * Validate a single adder state object against non-solar project rules.
 *
 * @returns An object of field-level error strings, or `null` if fully valid.
 */
export function validateNonSolarAdder(state: NonSolarAdderRecord): NonSolarAdderFieldErrors | null {
  if (isAdderCompatibleWithNsp(state)) {
    return null
  }

  const errors: NonSolarAdderFieldErrors = {}

  if (Number(state.type) !== 0) {
    errors.type = "Adder type must be 'Price' for non-solar projects."
  }

  if (!isFixedFormula(state.formula)) {
    errors.formula = "Formula must be 'Fixed' for non-solar projects."
  }

  if (!state.show_customer) {
    errors.show_customer = "'Show to customer' must be enabled for non-solar projects."
  }

  if (!state.display_as_line_item) {
    errors.display_as_line_item = "'Display as line item' must be enabled for non-solar projects."
  }

  if (!state.value_includes_tax) {
    errors.value_includes_tax =
      "Unit price must be tax inclusive (enable 'Including tax' on the library adder) for non-solar projects."
  }

  return errors
}
